import {
  FileHandlerAbstract, 
  IAddImageFromUrlParameters, 
  IFileHanderParameters, 
  ISaveToFileProgress, 
  FileHandlerActionEnum,
  IFileHandlerReplaceImageAction,
  IFileHandlerExcludeImageFromLicense,
  IFileHandlerPublishAsOerAction
} from './file-handler.abstract';
import {AddImageAsBase64Parameters} from '../assets/libs/office-open-xml-js/interfaces';
import {IReference, ISource} from '../assets/libs/office-open-xml-presentation-js/interfaces';
import {IImageWrapper} from '../models/image-wrapper';
import {Subject} from 'rxjs';
import {Document as DocumentModel} from '../models/document.model';
import {v4 as uuidv4} from 'uuid';
import {DataUrl, Utils, MimeType} from '../utils/utils';
import { UUID } from '../utils/utils';
import {PDFDocument, PDFName, PDFArray, PDFDict, PDFStream, PDFRef, rgb} from 'pdf-lib';
import {saveAs} from 'file-saver';

// see this https://resources.infosecinstitute.com/pdf-file-format-basic-structure/ to understand PDFs
// or https://www.oreilly.com/library/view/pdf-explained/9781449321581/ch04.html
// or https://web.archive.org/web/20141010035745/http://gnupdf.org/Introduction_to_PDF

// big fat props to https://stackoverflow.com/a/50769855/7618184 !!!!
import * as pdfjsLib from 'pdfjs-dist';
import {
  PDFDocumentProxy,
  getDocument as getPdfDocument,
  GlobalWorkerOptions as PdfjsGlobalWorkerOptions,
} from 'pdfjs-dist';
import {Licenses} from '../models/licenses';
import {License} from '../assets/libs/office-open-xml-js/license';

// Interface to store detailed image information
interface IPdfImage {
  pageNum: number;      // The page number (0-indexed)
  imageIndex: number;   // Index of the image on the page
  xref: number;         // Cross-reference number in the PDF
  dimensions: {
    width: number;
    height: number;
    ratio: number;
  };
  base64: DataUrl;
  source: string;       // Source path identifier
  internalId: number;   // Used for tracking
  pdfImage?: any;       // Reference to the PDF image object if needed
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  hash: string;         // Store the hash for better matching
  imgRef: string;       // Store the original imgRef for better matching
  uniqueId: string;     // Store the unique ID
}

export class PdfHandler extends FileHandlerAbstract {
  private _pdf: PDFDocumentProxy;
  private __inited: boolean;
  private _pdfArrayBuffer: ArrayBuffer;
  private _pdfDoc: PDFDocument;  // pdf-lib document
  private _extractedImages: Map<string, IPdfImage> = new Map(); // Map of extracted images by path
  private _replacements: Map<string, {original: IPdfImage, replacement: IImageWrapper}> = new Map();

  constructor(params: IFileHanderParameters) {
    super(params);
    this.__inited = false;
    this.__init(params);
  }

  /**
   * Initialize the PDF handler
   */
  private async __init(params: IFileHanderParameters): Promise<void> {
    if (!this.__inited) {
      this.__inited = true;
      this._pdfArrayBuffer = await new Response(params.file.file).arrayBuffer();
      
      // Initialize pdf.js document for image extraction
      this._pdf = await getPdfDocument(this._pdfArrayBuffer).promise;
      
      // Initialize pdf-lib document for editing
      this._pdfDoc = await PDFDocument.load(this._pdfArrayBuffer);
      
      // Set up document metadata
      const mimeType = new MimeType('application/pdf');
      const guid = await this.getGuid();
      this._document = new DocumentModel(guid, mimeType);
      this._subjectSubhandlerReady.next();

      const workerUrl = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
      console.log('[PdfHandler] PDF Worker URL configured:', workerUrl);
    } else {
      return Promise.reject('Already inited. Why do you want to init again?!');
    }
  }

  /**
   * Extract images from the PDF document
   */
  public async getImages(): Promise<IImageWrapper[]> {
    const images: IImageWrapper[] = [];
    const processedHashes = new Set<string>(); // Track processed images to avoid duplicates
    let internalIdCounter = 0;

    // Go through each page
    for (let pageNum = 0; pageNum < this._pdf.numPages; pageNum++) {
      const page = await this._pdf.getPage(pageNum + 1); // PDF pages are 1-indexed
      const viewport = page.getViewport({ scale: 1.0 });
      
      // Store page dimensions for later use
      const pageWidth = viewport.width;
      const pageHeight = viewport.height;
      
      // Get the operator list which contains all operations including image operations
      // @ts-ignore The function is present in the object
      const ops = await page.getOperatorList();

      const imageRefs = [];
      const imagePositions = new Map<string, {x: number, y: number, width: number, height: number}>();
      
      // First pass: Find all image operations and their positions/transforms
      let currentTransform = [1, 0, 0, 1, 0, 0]; // Default identity matrix
      
      for (let i = 0; i < ops.fnArray.length; i++) {
        // Track transform operations
        if (ops.fnArray[i] === pdfjsLib.OPS.transform) {
          currentTransform = ops.argsArray[i];
        }
        
        // Detect image operations
        if (
          ops.fnArray[i] === pdfjsLib.OPS.paintJpegXObject ||
          ops.fnArray[i] === pdfjsLib.OPS.paintImageXObject ||
          ops.fnArray[i] === pdfjsLib.OPS.paintXObject
        ) {
          const imgRef = ops.argsArray[i][0];
          imageRefs.push(imgRef);
          
          // Use the current transform for this image
          const [a, b, c, d, e, f] = currentTransform;
          
          // Store position data from the transform matrix
          // The transform is applied to the unit square (0,0)-(1,1)
          // We'll calculate the actual image dimensions later
          imagePositions.set(imgRef, {
            x: e, // Translate X
            y: f, // Translate Y
            width: Math.abs(a), // Scale X
            height: Math.abs(d)  // Scale Y
          });
          
          console.log(`Found image ${imgRef} on page ${pageNum + 1} with transform [${currentTransform.join(',')}]`);
        }
      }

      // Process each image reference
      for (let imgIdx = 0; imgIdx < imageRefs.length; imgIdx++) {
        const imgRef = imageRefs[imgIdx];
        
        // Get the image data from the page
        // @ts-ignore Object exists but TypeScript doesn't recognize it
        const imgObj = await page.objs.get(imgRef);
        
        if (!imgObj || !imgObj.data) {
          console.warn(`Image ${imgRef} on page ${pageNum + 1} has no data`);
          continue;
        }
        
        // Hash the image data to detect duplicates
        const hash = await this._hashImageData(imgObj.data);
        
        // Get PDF object reference for this image
        const xref = await this._findXRefForImage(pageNum, imgRef);
        
        // Get position information
        let position = imagePositions.get(imgRef);
        
        // If no position found, or the transform doesn't contain actual dimensions
        // (often happens when the transform just positions without scaling)
        if (!position || position.width <= 1 || position.height <= 1) {
          // Use image dimensions directly and center it
          position = {
            x: (pageWidth - imgObj.width) / 2, 
            y: (pageHeight - imgObj.height) / 2,
            width: imgObj.width,
            height: imgObj.height
          };
        } else {
          // If transform contains scale but not actual dimensions, apply image dimensions
          if (position.width === 1) position.width = imgObj.width;
          if (position.height === 1) position.height = imgObj.height;
        }
        
        // Account for PDF's bottom-left origin coordinate system
        // pdf-lib uses bottom-left (0,0), but we need to adjust for this
        position.y = pageHeight - position.y - position.height;
        
        console.log(`Image ${imgRef} position: x=${position.x}, y=${position.y}, w=${position.width}, h=${position.height}`);
        
        // Create a unique identifier for this image - combine page, position, dimensions and hash
        // This will help us match the exact same image during replacement
        const uniqueImageId = `page_${pageNum + 1}_image_${internalIdCounter}`;
        
        // Create image wrapper for the UI
        const imageWrapper: IImageWrapper = {
          internalId: internalIdCounter,
          base64: Utils.binaryImageDataToDataUrl(imgObj.data, imgObj.width, imgObj.height),
          dimension: {
            width: imgObj.width,
            height: imgObj.height,
            ratio: imgObj.width / imgObj.height
          },
          source: uniqueImageId
        };
        
        // Store detailed image info for later use
        const pdfImage: IPdfImage = {
          pageNum,
          imageIndex: imgIdx,
          xref,
          dimensions: imageWrapper.dimension,
          base64: imageWrapper.base64 as DataUrl,
          source: uniqueImageId,
          internalId: internalIdCounter,
          position,
          hash, // Store the hash for better matching
          imgRef, // Store the original imgRef for better matching
          uniqueId: uniqueImageId // Store the unique ID
        };
        
        this._extractedImages.set(uniqueImageId, pdfImage);
        images.push(imageWrapper);
        internalIdCounter++;
      }
    }

    console.log(`Extracted ${images.length} images from PDF`);
    return images;
  }

  /**
   * Find the XRef value for an image in the PDF structure
   */
  private async _findXRefForImage(pageNum: number, imgRef: string): Promise<number | null> {
    try {
      // Clean up imgRef if needed (sometimes it has extra characters)
      const cleanImgRef = imgRef.replace(/^[\/]?/, ''); // Remove leading slash if present
      
      // Get the page from pdf-lib
      const page = this._pdfDoc.getPage(pageNum);
      const pageDict = page.node;
      
      // Check for Resources → XObject
      if (pageDict.has(PDFName.of('Resources'))) {
        const resources = pageDict.get(PDFName.of('Resources'));
        if (resources instanceof PDFDict && resources.has(PDFName.of('XObject'))) {
          const xObjects = resources.get(PDFName.of('XObject'));
          
          if (xObjects instanceof PDFDict) {
            // Look for our image reference - try multiple matching strategies
            
            // Strategy 1: Direct name match (most precise)
            for (const [name, ref] of xObjects.entries()) {
              // The name is a PDFName, which has format /Name - we need to clean it
              const nameStr = name.toString().replace(/^\//, ''); // Remove leading slash
              
              if (nameStr === cleanImgRef && ref instanceof PDFRef) {
                console.log(`Found exact XObject match: ${nameStr} = ${cleanImgRef}, ref: ${ref.objectNumber}`);
                return ref.objectNumber;
              }
            }
            
            // Strategy 2: Case-insensitive match
            for (const [name, ref] of xObjects.entries()) {
              const nameStr = name.toString().replace(/^\//, '');
              
              if (nameStr.toLowerCase() === cleanImgRef.toLowerCase() && ref instanceof PDFRef) {
                console.log(`Found case-insensitive XObject match: ${nameStr} ~ ${cleanImgRef}, ref: ${ref.objectNumber}`);
                return ref.objectNumber;
              }
            }
            
            // Strategy 3: Partial match (last resort)
            for (const [name, ref] of xObjects.entries()) {
              const nameStr = name.toString();
              
              if ((nameStr.includes(cleanImgRef) || cleanImgRef.includes(nameStr.replace(/^\//, ''))) && ref instanceof PDFRef) {
                console.log(`Found partial XObject match: ${nameStr} contains ${cleanImgRef}, ref: ${ref.objectNumber}`);
                return ref.objectNumber;
              }
            }
            
            // If we got here, no matching name was found
            console.warn(`Could not find XObject named "${cleanImgRef}" in page ${pageNum+1}'s Resources`);
          }
        }
      }
      
      // If no match found, return null instead of a random timestamp
      console.error(`Failed to find XRef for image "${imgRef}" on page ${pageNum+1}`);
      return null;
    } catch (error) {
      console.error(`Error in _findXRefForImage:`, error);
      return null;
    }
  }

  /**
   * Create a simple hash of image data for duplicate detection
   */
  private async _hashImageData(data: Uint8ClampedArray): Promise<string> {
    // For simplicity, we'll just sample some bytes
    const samples = [];
    const step = Math.max(1, Math.floor(data.length / 100));
    
    for (let i = 0; i < data.length; i += step) {
      samples.push(data[i]);
    }
    
    return samples.join(',');
  }

  /**
   * Parse image path format "page_{pageNumber}_image_{imageRef}"
   */
  private parseImagePath(path: string): {page: number, id: number} | null {
    const match = path.match(/page_(\d+)_image_(\d+)/);
    if (match) {
      return {
        page: parseInt(match[1], 10),
        id: parseInt(match[2], 10)
      };
    }
    return null;
  }

  /**
   * Adds base64 encoded image to the PDF as a replacement
   */
  public async addBase64Image(image: AddImageAsBase64Parameters): Promise<any> {
    console.log("Adding base64 image for path:", image.nameAndFullPath);
    
    // Find the original image by path
    const originalImage = this._extractedImages.get(image.nameAndFullPath);
    
    if (!originalImage) {
      console.error("Original image not found for path:", image.nameAndFullPath);
      return;
    }
    
    // Ensure we always create a valid DataUrl object
    let dataUrlObj: DataUrl;
    if (image.imageAsBase64 && image.imageAsBase64.startsWith('data:')) {
      dataUrlObj = DataUrl.fromString(image.imageAsBase64);
    } else {
      dataUrlObj = new DataUrl('image/png', image.imageAsBase64, true);
    }
    
    // Create a wrapper for the replacement image
    const newImage: IImageWrapper = {
      internalId: originalImage.internalId,
      base64: dataUrlObj,
      dimension: originalImage.dimensions,
      source: originalImage.source
    };
    
    // Store the replacement
    this._replacements.set(image.nameAndFullPath, {
      original: originalImage,
      replacement: newImage
    });
    
    console.log(`Stored replacement for image ${originalImage.internalId} on page ${originalImage.pageNum + 1}`);
  }

  /**
   * Add image from URL
   */
  public async addImageFromUrl(image: IAddImageFromUrlParameters): Promise<any> {
    const dataUrl = await DataUrl.fromImageUrl(image.urlToImage);
    return this.addBase64Image({
      nameAndFullPath: image.nameAndFullPath,
      imageAsBase64: dataUrl.getData()
    });
  }

  /**
   * Add sources information
   */
  public async addSources(sources: ISource[]): Promise<void> {
    // Will be implemented as part of saveToFile
  }

  /**
   * Add bibliographic references
   */
  public async addBibRefsToImages(references: IReference[]): Promise<void> {
    // Will be implemented as part of saveToFile
  }

  /**
   * Reset file state
   */
  public resetFileState(): void {
    this._replacements.clear();
  }

  /**
   * Save the PDF with replaced images - using DIRECT image replacement
   */
  public async saveToFile(targetLicense: License, logSubject?: Subject<ISaveToFileProgress>): Promise<void> {
    if (logSubject) {
      logSubject.next({
        text: 'Processing PDF and replacing images...',
          action: FileHandlerActionEnum.ZIPPING_FILE
      });
    }

    try {
      await this._executeActionQueue(logSubject);
      
    if (this._replacements.size === 0) {
      if (logSubject) {
        logSubject.next({
            text: 'No changes made to the PDF file.',
            action: FileHandlerActionEnum.READY_TO_DOWNLOAD
        });
      }
      saveAs(new Blob([this._pdfArrayBuffer], {type: 'application/pdf'}), this._file.file.name);
      return;
    }

      console.log("Number of replacements to apply:", this._replacements.size);
      
      // Create a map of replacements by page
      const replacementsByPage = new Map<number, Array<{original: IPdfImage, replacement: IImageWrapper}>>();
      let hasWarnings = false;
      
      for (const [path, { original, replacement }] of this._replacements.entries()) {
        const pageNum = original.pageNum;
        if (!replacementsByPage.has(pageNum)) {
          replacementsByPage.set(pageNum, []);
        }
        replacementsByPage.get(pageNum)!.push({ original, replacement });
        console.log(`Queued replacement for page ${pageNum + 1}, image ${original.internalId}`);
      }
      
      // Load the PDF document for direct modification
      const { pdfBytes, hasWarnings: directReplaceWarnings } = await this._directReplaceImages(this._pdfArrayBuffer, replacementsByPage, logSubject);
      
      // Add attribution page if needed
      const pdfDoc = await PDFDocument.load(pdfBytes);
      
      if (this.disclaimerText) {
        if (logSubject) {
          logSubject.next({
            text: 'Adding attribution page...',
            action: FileHandlerActionEnum.ADD_SOURCE_CREDITS
          });
        }
        
        const attributionPage = pdfDoc.addPage();
        const { width, height } = attributionPage.getSize();
        attributionPage.drawText('Attribution and License Information', {
          x: 50,
          y: height - 50,
          size: 18
        });
        attributionPage.drawText(this.disclaimerText, {
          x: 50,
          y: height - 80,
          size: 12,
          maxWidth: width - 100,
          lineHeight: 16
        });
        attributionPage.drawText(`Target License: ${targetLicense.shortName}`, {
          x: 50,
          y: height - 150,
          size: 12
        });
      }
      
      // Save the final PDF with attribution
      const finalPdfBytes = await pdfDoc.save();
      
      // Check if there were pages where we couldn't replace images
      if (directReplaceWarnings) {
        if (logSubject) {
          logSubject.next({
            text: '⚠️ WARNING: Some images could not be replaced in this PDF.\n\n' +
                  'This PDF appears to use a non-standard format for storing images, which makes direct replacement impossible. ' +
                  'The images were successfully extracted but cannot be replaced in the current PDF structure.\n\n' +
                  'Recommendations:\n' +
                  '1. Try recreating this PDF from the original document using Microsoft Word, Google Docs, or Adobe Acrobat\n' +
                  '2. Some PDF files created from scans or specialized software may not support direct image replacement\n' +
                  '3. For best results, use PDF files created directly from Office applications',
            action: FileHandlerActionEnum.READY_TO_DOWNLOAD
          });
        }
      } else {
      if (logSubject) {
        logSubject.next({
            text: 'PDF file is ready to download',
            action: FileHandlerActionEnum.READY_TO_DOWNLOAD
        });
        }
      }
      
      // Offer the file for download
      saveAs(new Blob([finalPdfBytes], {type: 'application/pdf'}), this._file.file.name);
      
    } catch (error) {
      console.error('Failed to save PDF file:', error);
      if (logSubject) {
        logSubject.next({
          text: 'Failed to create PDF file: ' + error.message,
            action: FileHandlerActionEnum.ABORTING_ON_ERROR
        });
      }
      throw error;
    }
  }

  /**
   * Direct replacement of images within the PDF streams
   * This simulates the behavior of PyMuPDF's page.replace_image() function
   */
  private async _directReplaceImages(
    pdfBuffer: ArrayBuffer,
    replacementsByPage: Map<number, Array<{original: IPdfImage, replacement: IImageWrapper}>>,
    logSubject?: Subject<ISaveToFileProgress>
  ): Promise<{pdfBytes: Uint8Array, hasWarnings: boolean}> {
    // Load the PDF document for direct modification
    let pdfDoc = await PDFDocument.load(pdfBuffer);
    
    // Track if we made any replacements for debugging
    let replacementsApplied = 0;
    let hasWarnings = false;
    
    // Process each page that has replacements
    for (const [pageIndex, replacements] of replacementsByPage.entries()) {
      if (logSubject) {
        logSubject.next({
          text: `Processing page ${pageIndex + 1}...`,
          action: FileHandlerActionEnum.REPLACE_IMAGE_BY_BASE64
        });
      }
      
      const page = pdfDoc.getPage(pageIndex);
      const pageDict = page.node;
      
      // Check if page has Resources
      if (!pageDict.has(PDFName.of('Resources'))) {
        console.warn(`Page ${pageIndex + 1} has no Resources, using alternate approach`);
        const result = await this._tryAlternateImageReplacement(pdfDoc, page, pageIndex, replacements);
        if (!result) hasWarnings = true;
        continue;
      }
      
      // Get Resources dictionary
      const resources = pageDict.get(PDFName.of('Resources'));
      if (!(resources instanceof PDFDict)) {
        console.warn(`Resources is not a dictionary on page ${pageIndex + 1}, using alternate approach`);
        const result = await this._tryAlternateImageReplacement(pdfDoc, page, pageIndex, replacements);
        if (!result) hasWarnings = true;
        continue;
      }
      
      // Check for XObject dictionary
      const hasXObjects = resources.has(PDFName.of('XObject'));
      
      // If no XObjects dictionary, look for images in content stream
      if (!hasXObjects) {
        console.warn(`Page ${pageIndex + 1} has no XObjects, using alternate approach`);
        const result = await this._tryAlternateImageReplacement(pdfDoc, page, pageIndex, replacements);
        if (!result) hasWarnings = true;
        continue;
      }
      
      // Get the XObjects dictionary
      const xObjects = resources.get(PDFName.of('XObject'));
      if (!(xObjects instanceof PDFDict)) {
        console.warn(`XObject is not a dictionary on page ${pageIndex + 1}, using alternate approach`);
        const result = await this._tryAlternateImageReplacement(pdfDoc, page, pageIndex, replacements);
        if (!result) hasWarnings = true;
        continue;
      }
      
      // Log all XObject entries to help with debugging
      console.log(`XObjects on page ${pageIndex + 1}:`);
      const xObjectEntries: Array<[PDFName, PDFRef]> = [];
      for (const [name, ref] of xObjects.entries()) {
        if (ref instanceof PDFRef) {
          // Try to get the object to inspect what type it is
          const obj = pdfDoc.context.lookup(ref);
          const objType = obj?.constructor?.name || 'unknown';
          const isStream = obj instanceof PDFStream;
          const isDict = obj instanceof PDFDict;
          
          // Log object info for debugging
          console.log(`- ${name.toString()} → object ${ref.objectNumber} (type: ${objType}, isStream: ${isStream}, isDict: ${isDict})`);
          
          // Check if it's an image XObject
          let isImage = false;
          let imageSize = '';
          
          if (isDict && obj.has(PDFName.of('Subtype')) && obj.get(PDFName.of('Subtype')).toString() === '/Image') {
            isImage = true;
            if (obj.has(PDFName.of('Width')) && obj.has(PDFName.of('Height'))) {
              imageSize = `${obj.get(PDFName.of('Width'))}x${obj.get(PDFName.of('Height'))}`;
            }
          } else if (isStream && obj.dict && obj.dict.has(PDFName.of('Subtype')) && 
                    obj.dict.get(PDFName.of('Subtype')).toString() === '/Image') {
            isImage = true;
            if (obj.dict.has(PDFName.of('Width')) && obj.dict.has(PDFName.of('Height'))) {
              imageSize = `${obj.dict.get(PDFName.of('Width'))}x${obj.dict.get(PDFName.of('Height'))}`;
            }
          }
          
          if (isImage) {
            console.log(`  This is an Image XObject (${imageSize})`);
          }
          
          xObjectEntries.push([name as PDFName, ref as PDFRef]);
        } else {
          console.log(`- ${name.toString()} → not a reference (type: ${ref?.constructor?.name || 'unknown'})`);
        }
      }
      
      // Process each replacement for this page
      for (const { original, replacement } of replacements) {
        // Prepare the replacement image data
        if (!replacement.base64) {
          console.error('Replacement image is missing base64 data');
          continue;
        }
        
        const imgData = replacement.base64.toString();
        const base64Data = imgData.split(',')[1];
        if (!base64Data) {
          console.error('Invalid base64 data format');
          continue;
        }
        
        const replacementBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
        const isJpeg = imgData.includes('image/jpeg') || imgData.includes('image/jpg');
        
        // Find a matching XObject for this image
        let targetXObjectName: PDFName | null = null;
        let targetXObjectRef: PDFRef | null = null;
        
        // Log details about the original image for debugging
        console.log(`Looking for image replacement match for:`, {
          pageNum: original.pageNum + 1,
          imageIndex: original.imageIndex,
          xref: original.xref,
          hash: original.hash?.substring(0, 20) + '...',
          imgRef: original.imgRef,
          dimensions: `${original.dimensions.width}x${original.dimensions.height}`
        });
        
        // APPROACH 1: Try to match by xref if we have one
        if (original.xref !== null) {
          for (const [name, ref] of xObjectEntries) {
            if (ref.objectNumber === original.xref) {
              targetXObjectName = name;
              targetXObjectRef = ref;
              console.log(`Found XObject match by XRef: ${name.toString()} (${ref.objectNumber})`);
              break;
            }
          }
        }
        
        // APPROACH 2: Try to match by original imgRef name - this is the most reliable when available
        if (!targetXObjectName && original.imgRef) {
          // Clean imgRef name - sometimes it has a leading slash
          const cleanImgRef = original.imgRef.replace(/^\//, '');
          
          for (const [name, ref] of xObjectEntries) {
            const nameStr = name.toString().replace(/^\//, '');
            if (nameStr === cleanImgRef) {
              targetXObjectName = name;
              targetXObjectRef = ref;
              console.log(`Found XObject match by imgRef name: ${name.toString()} = ${cleanImgRef}`);
              break;
            }
          }
        }
        
        // APPROACH 3: Try to match by image dimensions when there are multiple images
        if (!targetXObjectName && xObjectEntries.length > 1) {
          // Get all image objects for dimension comparison
          const imageObjects = [];
          
          for (const [name, ref] of xObjectEntries) {
            const obj = pdfDoc.context.lookup(ref);
            let dimensions = null;
            
            // Extract dimensions from either dict or stream
            if (obj instanceof PDFDict && obj.has(PDFName.of('Width')) && obj.has(PDFName.of('Height'))) {
              dimensions = {
                width: Number(obj.get(PDFName.of('Width'))),
                height: Number(obj.get(PDFName.of('Height')))
              };
            } else if (obj instanceof PDFStream && obj.dict && 
                      obj.dict.has(PDFName.of('Width')) && obj.dict.has(PDFName.of('Height'))) {
              dimensions = {
                width: Number(obj.dict.get(PDFName.of('Width'))),
                height: Number(obj.dict.get(PDFName.of('Height')))
              };
            }
            
            if (dimensions) {
              imageObjects.push({ name, ref, dimensions });
            }
          }
          
          // Find best dimensional match
          if (imageObjects.length > 0) {
            let bestMatch = null;
            let bestMatchScore = Infinity;
            
            for (const imgObj of imageObjects) {
              // Calculate a score based on how close the dimensions are
              const widthDiff = Math.abs(imgObj.dimensions.width - original.dimensions.width);
              const heightDiff = Math.abs(imgObj.dimensions.height - original.dimensions.height);
              const score = widthDiff + heightDiff;
              
              if (score < bestMatchScore) {
                bestMatchScore = score;
                bestMatch = imgObj;
              }
            }
            
            if (bestMatch && bestMatchScore < 20) { // Allow some small difference
              targetXObjectName = bestMatch.name;
              targetXObjectRef = bestMatch.ref;
              console.log(`Found XObject match by dimensions: ${bestMatch.name.toString()} (dimensions: ${bestMatch.dimensions.width}x${bestMatch.dimensions.height}, score: ${bestMatchScore})`);
            }
          }
        }
        
        // APPROACH 4: Fall back to image index if no better match found
        if (!targetXObjectName && original.imageIndex !== undefined) {
          // If we have multiple XObjects and this was the Nth image we extracted,
          // try matching with the Nth XObject
          if (xObjectEntries.length > original.imageIndex) {
            const [name, ref] = xObjectEntries[original.imageIndex];
            targetXObjectName = name;
            targetXObjectRef = ref;
            console.log(`Found XObject match by index: ${name.toString()} (index ${original.imageIndex})`);
          }
        }
        
        // APPROACH 5: Try to find any image XObject as last resort
        if (!targetXObjectName) {
          // Try to identify image XObjects by examining their stream data
          for (const [name, ref] of xObjectEntries) {
            const obj = pdfDoc.context.lookup(ref);
            
            // Check if the object is either a dictionary or a stream with dictionary
            let isImageObject = false;
            
            if (obj instanceof PDFDict && obj.has(PDFName.of('Subtype'))) {
              const subtype = obj.get(PDFName.of('Subtype'));
              isImageObject = subtype instanceof PDFName && subtype.toString() === '/Image';
            } else if (obj instanceof PDFStream && obj.dict && obj.dict.has(PDFName.of('Subtype'))) {
              const subtype = obj.dict.get(PDFName.of('Subtype'));
              isImageObject = subtype instanceof PDFName && subtype.toString() === '/Image';
            }
            
            if (isImageObject) {
              targetXObjectName = name;
              targetXObjectRef = ref;
              console.log(`Found image XObject: ${name.toString()}`);
              break;
            }
          }
        }
        
        // If we found a target XObject, replace its image data
        if (targetXObjectName && targetXObjectRef) {
          try {
            // Fetch the XObject dictionary or stream
            const xObject = pdfDoc.context.lookup(targetXObjectRef);
            
            // In PDFs, image XObjects are typically PDFStream objects with dictionary entries
            // So we need to check for both PDFDict and PDFStream
            if (!(xObject instanceof PDFDict) && !(xObject instanceof PDFStream)) {
              console.error(`XObject ${targetXObjectName.toString()} is not a dictionary or stream, type:`, xObject?.constructor?.name);
              continue;
            }
            
            // Verify this is an image object by checking the Subtype
            let subtype;
            if (xObject instanceof PDFDict) {
              subtype = xObject.get(PDFName.of('Subtype'));
            } else if (xObject instanceof PDFStream) {
              // For streams, we access dictionary properties via the 'dict' property
              subtype = xObject.dict.get(PDFName.of('Subtype'));
            }
            
            if (!subtype || subtype.toString() !== '/Image') {
              console.error(`XObject ${targetXObjectName.toString()} is not an image (subtype: ${subtype?.toString()})`);
              continue;
            }
            
            console.log(`Identified valid image XObject: ${targetXObjectName.toString()}`);
            
            // Get the original image properties we need to maintain
            let width, height, bitsPerComponent, colorSpace;
            
            if (xObject instanceof PDFDict) {
              width = Number(xObject.get(PDFName.of('Width')));
              height = Number(xObject.get(PDFName.of('Height')));
              bitsPerComponent = xObject.has(PDFName.of('BitsPerComponent')) ? 
                Number(xObject.get(PDFName.of('BitsPerComponent'))) : 8;
              colorSpace = xObject.get(PDFName.of('ColorSpace'));
            } else if (xObject instanceof PDFStream) {
              width = Number(xObject.dict.get(PDFName.of('Width')));
              height = Number(xObject.dict.get(PDFName.of('Height')));
              bitsPerComponent = xObject.dict.has(PDFName.of('BitsPerComponent')) ? 
                Number(xObject.dict.get(PDFName.of('BitsPerComponent'))) : 8;
              colorSpace = xObject.dict.get(PDFName.of('ColorSpace'));
            }
            
            if (!colorSpace) {
              colorSpace = PDFName.of('DeviceRGB'); // Default
            }
            
            // Embed the new image and get its ref
            const newImageRef = isJpeg ? 
              await pdfDoc.embedJpg(replacementBytes) : 
              await pdfDoc.embedPng(replacementBytes);
            
            console.log(`Successfully embedded replacement image, replacing XObject reference...`);
            
            // The critical step: Replace the old XObject reference with the new image reference
            xObjects.set(targetXObjectName, newImageRef.ref);
            
            // If successful, log and increment counter
            console.log(`REPLACED Image XObject ${targetXObjectName.toString()} with new image`);
            replacementsApplied++;
          } catch (error) {
            console.error(`Error replacing image in XObject ${targetXObjectName.toString()}:`, error);
          }
        } else {
          console.error(`Could not find XObject for image on page ${pageIndex + 1}`);
        }
      }
    }
    
    // Final log message
    console.log(`Applied ${replacementsApplied} direct image replacements`);
    
    // Save the modified PDF and include warning flag
    return {
      pdfBytes: await pdfDoc.save(),
      hasWarnings
    };
  }

  /**
   * Try to find and replace images even in cases where they're not standard XObjects
   * This is a more limited approach but can work for some PDFs
   */
  private async _tryAlternateImageReplacement(
    pdfDoc: PDFDocument, 
    page: any, 
    pageIndex: number, 
    replacements: Array<{original: IPdfImage, replacement: IImageWrapper}>
  ): Promise<boolean> {
    // This is a limited approach for PDFs that don't use standard XObjects
    // We'll try to examine the page's content stream for image data
    
    console.log(`Attempting alternate image replacement for page ${pageIndex + 1}`);
    
    // Get the page's content stream
    const pageDict = page.node;
    if (!pageDict.has(PDFName.of('Contents'))) {
      console.error(`Page ${pageIndex + 1} has no Contents`);
      return false;
    }
    
    // Log that we couldn't replace directly
    console.warn(`Couldn't find standard XObjects on page ${pageIndex + 1} for direct replacement.`);
    console.warn(`Your PDF uses a different method for storing images.`);
    console.warn(`For best results, consider recreating this PDF with Microsoft Word or Adobe Acrobat.`);
    
    // Signal that we couldn't replace images on this page
    return false;
  }

  /**
   * Override the executeActionQueue method to properly handle PDF-specific actions
   */
  protected async _executeActionQueue(logSubject?: Subject<ISaveToFileProgress>): Promise<any> {
    console.log('PDF action queue execution - items:', this._actionQueue.length);
    
    // Get a copy of the action queue to process
    const actionQueue = [...this._actionQueue];
    
    // Process each action in the queue
    for (let i = 0; i < actionQueue.length; i++) {
      const action = actionQueue[i];
      
      if (logSubject) {
        logSubject.next({
          text: `Processing action ${i + 1} of ${actionQueue.length}...`,
          action: FileHandlerActionEnum.ZIPPING_FILE,
          current: i + 1,
          total: actionQueue.length
        });
      }
      
      switch (action.action) {
        case FileHandlerActionEnum.REPLACE_IMAGE_BY_URL:
        case FileHandlerActionEnum.REPLACE_IMAGE_BY_BASE64: {
          const replaceAction = action as IFileHandlerReplaceImageAction;
          console.log(`Processing replace action for ${replaceAction.toReplace.source}`);
          
          // Find the original image in our extracted images
          const originalImage = this._extractedImages.get(replaceAction.toReplace.source);
          if (originalImage) {
            // Ensure the replacement image has a valid base64 property
            const replacementImage = { ...replaceAction.replacingBy };
            
            // Check if the image has been edited
            if (replacementImage.edited) {
              console.log('Using edited image data for replacement');
              // If edited data exists, prioritize it for the replacement
              if (replacementImage.edited.base64) {
                // Make the edited base64 the primary base64 for replacement
                replacementImage.base64 = replacementImage.edited.base64;
              }
            }
            
            // Check if base64 is missing or not a DataUrl object
            if (!replacementImage.base64 || typeof replacementImage.base64 === 'string') {
              console.log('Converting base64 data to proper format');
              
              // Get the base64 string, whether it's stored as a string or in a DataUrl object
              let base64String = '';
              if (typeof replacementImage.base64 === 'string') {
                base64String = replacementImage.base64;
              } else if (replacementImage.base64 && typeof replacementImage.base64.toString === 'function') {
                base64String = replacementImage.base64.toString();
              }
              
              // Create a proper DataUrl object
              if (base64String) {
                if (base64String.startsWith('data:')) {
                  replacementImage.base64 = DataUrl.fromString(base64String);
                } else {
                  // If it's just the base64 data without the data URL prefix
                  replacementImage.base64 = new DataUrl('image/png', base64String, true);
                }
              } else {
                // If we can't get base64 data from the replacement image,
                // try to fetch it using the source URL if available
                if (replacementImage.source && replacementImage.source.startsWith('http')) {
                  try {
                    replacementImage.base64 = await DataUrl.fromImageUrl(replacementImage.source);
                    console.log('Successfully fetched image from URL', replacementImage.source);
                  } catch (error) {
                    console.error('Failed to fetch image from URL', replacementImage.source, error);
                  }
                } else {
                  console.error('Cannot process replacement: no valid base64 data or source URL');
                }
              }
            }
            
            // Store the replacement in our map to be used later
            this._replacements.set(replaceAction.toReplace.source, {
              original: originalImage,
              replacement: replacementImage
            });
            
            console.log(`Queued replacement for ${replaceAction.toReplace.source}`);
          } else {
            console.error(`Original image not found for ${replaceAction.toReplace.source}`);
          }
          break;
        }
        
        case FileHandlerActionEnum.EXCLUDE_FROM_LICENSE: {
          // Handle exclusion from license similar to abstract class
          const excludeAction = action as IFileHandlerExcludeImageFromLicense;
          let excludedFirstImageWithMention = false;
          
          if (!excludeAction.withoutNotice) {
            let textToAdd = excludedFirstImageWithMention ? ',\n ' : `Excluded are: `;
            textToAdd += excludeAction.noticeText;
            excludedFirstImageWithMention = true;
            this.additionalDisclaimerText += textToAdd;
          }
          break;
        }
        
        case FileHandlerActionEnum.PUBLISH_AS_OER: {
          // Handle publish as OER - add to attribution info
          const publishAction = action as IFileHandlerPublishAsOerAction;
          // For PDFs, we just need to update the attribution info
          console.log(`Processing publish as OER for ${publishAction.image.source}`);
          break;
        }
        
        default:
          console.log(`Unhandled action type: ${action.action}`);
      }
    }
    
    // Update disclaimer text if needed
    if (this.additionalDisclaimerText) {
      this.disclaimerText += "\n" + this.additionalDisclaimerText;
      this.additionalDisclaimerText += "\nThe rights are reserved by the respective owners.";
    }
    
    return Promise.resolve();
  }

  /**
   * Get file GUID
   */
  protected async getGuid(): Promise<UUID> {
    const fingerprints = this._pdf.fingerprints;
    const guid = fingerprints && fingerprints.length > 0 ? fingerprints[0] : null;
    if (!guid) {
      return new UUID(uuidv4());
    } else {
      return new UUID(guid);
    }
  }

  /**
   * Get file author name
   */
  public async getFileAuthorName(): Promise<string> {
    const metadata = await this._pdf.getMetadata();
    const author = metadata.info ? metadata.info['Author'] : null;
    return author ? author : 'unknown';
  }
}
